import { Loader2, Search } from 'lucide-react';

import { Badge, Checkbox, Input } from '@design-system';

import { getRecipientDisplayName, getRecipientOrgName } from './use-share-client-dialog';

interface ShareRecipientPickerProps {
  recipients: SupervisorAdvisorRelationship[];
  selectedIds: string[];
  alreadySharedIds: Set<string>;
  onToggle: (recipient: SupervisorAdvisorRelationship) => void;
  search: string;
  onSearchChange: (value: string) => void;
  isLoading: boolean;
  hasNoRecipients: boolean;
  loadingLabel: string;
  noRecipientsLabel: string;
  searchPlaceholder: string;
  noMatchesLabel: string;
}

// The searchable, checkbox-per-person list shared by the base share flow and the Edward Jones
// collaborate flow. Purely presentational — copy comes from the caller so each flow keeps its own
// wording without a variant flag in here.
export function ShareRecipientPicker({
  recipients,
  selectedIds,
  alreadySharedIds,
  onToggle,
  search,
  onSearchChange,
  isLoading,
  hasNoRecipients,
  loadingLabel,
  noRecipientsLabel,
  searchPlaceholder,
  noMatchesLabel,
}: ShareRecipientPickerProps) {
  return (
    <>
      <div className="relative mb-3">
        <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="max-h-72 overflow-y-auto rounded-md border">
        {isLoading ? (
          <div className="text-foreground flex items-center justify-center gap-2 py-8 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {loadingLabel}
          </div>
        ) : hasNoRecipients ? (
          <div className="text-foreground py-8 text-center text-sm">{noRecipientsLabel}</div>
        ) : recipients.length === 0 ? (
          <div className="text-foreground py-8 text-center text-sm">
            {noMatchesLabel} "{search}".
          </div>
        ) : (
          <ul className="divide-border divide-y">
            {recipients.map(rel => {
              const id = `supervisor-${rel.advisorId}`;
              const orgName = getRecipientOrgName(rel);
              const checked = selectedIds.includes(rel.advisorId);
              return (
                <li key={rel.advisorId}>
                  <label
                    htmlFor={id}
                    className="hover:bg-muted flex cursor-pointer items-start gap-3 p-3 transition-colors"
                  >
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={() => onToggle(rel)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-950">
                        {getRecipientDisplayName(rel)}
                      </p>
                      <p className="text-foreground truncate text-xs">{rel.advisorEmail}</p>
                      {orgName && (
                        <p className="text-muted-foreground truncate text-xs">{orgName}</p>
                      )}
                      {alreadySharedIds.has(rel.advisorId) && (
                        <Badge variant="success">Already has access</Badge>
                      )}
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
